// NOTE (Bot V2 stats — deferred): this node topology is mobile-app shaped. The
// WhatsApp bot emits the same `create-booking-tunnel-*` event names, so most
// funnel nodes populate when source=whatsapp, but bot-specific node accuracy
// (e.g. the homepage/restaurants-tab opening node, venue-book-pressed) is
// approximate. Dynamic per-segment node adaptation is deferred to a dedicated
// session — see the Bot V2 stats ticket.
import { VenueAnalyticsGraphBookingConversion } from '@models/venue/VenueAnalyticsGraph'

export type BookingConversionSankeyData = {
  nodes: {
    id: number
    label: string
    data: {
      imageUrl?: string
      loss: number
      count?: number
      isSuccessTerminaison: boolean
      isFailTerminaison: boolean
    }
  }[]
  links: { source: number; target: number; value: number }[]
  totalLoss: number
}

export enum BookingConversionSankeyNodeKeys {
  Homepage = 'homepage',
  VenueBookPressed = 'venue-book-pressed',
  Parameters = 'parameters',
  Slots = 'slots',
  Confirm = 'confirm',
  Loading = 'loading',
  Success = 'success',
  Failure = 'failure',
}

export const transformToSankeyData = (
  data: VenueAnalyticsGraphBookingConversion[]
): BookingConversionSankeyData => {
  if (!data || data.length === 0) {
    return { nodes: [], links: [], totalLoss: 0 }
  }

  const mapNodeData = (
    originalName: string
  ): { label: string; imageUrl?: string } => {
    switch (originalName) {
      case BookingConversionSankeyNodeKeys.Homepage:
        return {
          label: 'Home page',
          imageUrl: '/assets/sankeyImages/homepage.png',
        }
      case BookingConversionSankeyNodeKeys.VenueBookPressed:
        return {
          label: 'Venue page',
          imageUrl: '/assets/sankeyImages/venue-book-pressed.png',
        }
      case BookingConversionSankeyNodeKeys.Parameters:
        return {
          label: 'Parameters',
          imageUrl: '/assets/sankeyImages/parameters.png',
        }
      case BookingConversionSankeyNodeKeys.Slots:
        return {
          label: 'Slots',
          imageUrl: '/assets/sankeyImages/slots.png',
        }
      case BookingConversionSankeyNodeKeys.Confirm:
        return {
          label: 'Confirm',
          imageUrl: '/assets/sankeyImages/confirm.png',
        }
      case BookingConversionSankeyNodeKeys.Loading:
        return {
          label: 'Loading',
          imageUrl: '/assets/sankeyImages/loading.png',
        }
      case BookingConversionSankeyNodeKeys.Success:
        return {
          label: 'Success',
          imageUrl: '/assets/sankeyImages/success.png',
        }
      case BookingConversionSankeyNodeKeys.Failure:
        return {
          label: 'Failure',
        }
      default:
        return {
          label: originalName,
        }
    }
  }

  const LOST_NODE_ID = 8

  // Réindexer les nodes pour commencer à 1
  let nodeIdCounter = 1
  const idMapping: Record<number, number> = {}

  const nodes: BookingConversionSankeyData['nodes'] = data
    .filter((i) => i.name !== BookingConversionSankeyNodeKeys.Failure)
    .map(({ id, name, count }) => {
      const { label: mappedLabel, imageUrl } = mapNodeData(name)
      const newId = nodeIdCounter++
      idMapping[id] = newId

      const isSuccessNode = name === BookingConversionSankeyNodeKeys.Success

      return {
        id: newId,
        label: mappedLabel,
        data: {
          imageUrl,
          loss: 0,
          count,
          isSuccessTerminaison: isSuccessNode,
          isFailTerminaison: false,
        },
      }
    })

  nodes.push({
    id: LOST_NODE_ID,
    label: 'Lost',
    data: {
      loss: 100,
      isSuccessTerminaison: false,
      isFailTerminaison: true,
    },
  })

  const links: BookingConversionSankeyData['links'] = []

  const get = (name: string): VenueAnalyticsGraphBookingConversion =>
    data.find((d) => d.name === name)!

  const getId = (name: string): number => {
    const item = get(name)
    return idMapping[item.id]
  }

  const setLoss = (nodeId: number, loss: number) => {
    const node = nodes.find((n) => n.id === nodeId)
    if (node && node.data) {
      node.data.loss = loss
    }
  }

  // Homepage → Confirm
  const homepage = get(BookingConversionSankeyNodeKeys.Homepage)
  const venueBookPressed = get(BookingConversionSankeyNodeKeys.VenueBookPressed)
  const parameters = get(BookingConversionSankeyNodeKeys.Parameters)
  const slots = get(BookingConversionSankeyNodeKeys.Slots)
  const confirm = get(BookingConversionSankeyNodeKeys.Confirm)
  const loading = get(BookingConversionSankeyNodeKeys.Loading)
  const failure = get(BookingConversionSankeyNodeKeys.Failure)
  const success = get(BookingConversionSankeyNodeKeys.Success)

  const homepageId = getId(homepage.name)
  const venueBookPressedId = getId(venueBookPressed.name)
  const parametersId = getId(parameters.name)
  const slotsId = getId(slots.name)
  const confirmId = getId(confirm.name)
  const loadingId = getId(loading.name)
  const successId = getId(success.name)

  links.push({
    source: homepageId,
    target: confirmId,
    value: homepage.count,
  })
  setLoss(homepageId, 0)

  // Venue booked pressed → Parameters → Slots → Confirm

  links.push({
    source: venueBookPressedId,
    target: parametersId,
    value: parameters.count,
  })
  setLoss(venueBookPressedId, 0)

  links.push({
    source: parametersId,
    target: slotsId,
    value: slots.count,
  })

  const homepageToConfirm = homepage?.count ?? 0
  const confirmInputFromSlot = Math.max(0, confirm.count - homepageToConfirm)

  // Flux réel Slot → Confirm
  links.push({
    source: slotsId,
    target: confirmId,
    value: confirmInputFromSlot,
  })

  // Confirm → Loading → Failure / Success + Lost
  links.push({
    source: confirmId,
    target: loadingId,
    value: loading.count,
  })

  links.push({
    source: loadingId,
    target: successId,
    value: success.count,
  })

  // Calcul des pertes et ajout des liens vers "Lost"

  // Perte entre Loading et Success/Failure (utilisateurs perdus pendant le chargement)
  const lossAtLoadingToSuccessOrFailure = Math.max(
    0,
    loading.count - success.count - failure.count
  )
  if (lossAtLoadingToSuccessOrFailure > 0) {
    links.push({
      source: loadingId,
      target: LOST_NODE_ID,
      value: lossAtLoadingToSuccessOrFailure,
    })
    setLoss(
      loadingId,
      Math.ceil((lossAtLoadingToSuccessOrFailure / loading.count) * 100)
    )
  } else {
    setLoss(loadingId, 0)
  }

  // Échecs de réservation (Failure)
  if (failure.count > 0) {
    links.push({
      source: loadingId,
      target: LOST_NODE_ID,
      value: failure.count,
    })
    setLoss(
      loadingId,
      Math.ceil((lossAtLoadingToSuccessOrFailure / loading.count) * 100)
    )
  } else {
    setLoss(loadingId, 0)
  }

  // Perte entre Confirm et Loading (utilisateurs qui abandonnent après confirmation)
  const lossAtConfirmToLoading = Math.max(0, confirm.count - loading.count)
  if (lossAtConfirmToLoading > 0 || failure.count > 0) {
    links.push({
      source: confirmId,
      target: LOST_NODE_ID,
      value: lossAtConfirmToLoading,
    })
    setLoss(
      confirmId,
      Math.ceil((lossAtConfirmToLoading / confirm.count) * 100)
    )
  } else {
    setLoss(confirmId, 0)
  }

  // Perte entre Slots et Confirm (utilisateurs qui ne confirment pas après avoir vu les créneaux)
  const lossAtSlotsToConfirm = Math.max(0, slots.count - confirmInputFromSlot)
  if (lossAtSlotsToConfirm > 0) {
    links.push({
      source: slotsId,
      target: LOST_NODE_ID,
      value: lossAtSlotsToConfirm,
    })
    setLoss(slotsId, Math.ceil((lossAtSlotsToConfirm / slots.count) * 100))
  } else {
    setLoss(slotsId, 0)
  }

  // Perte entre Parameters et Slots (utilisateurs qui abandonnent après les paramètres)
  const lossAtParametersToSlots = Math.max(0, parameters.count - slots.count)
  if (lossAtParametersToSlots > 0) {
    links.push({
      source: parametersId,
      target: LOST_NODE_ID,
      value: lossAtParametersToSlots,
    })
  }
  setLoss(
    parametersId,
    Math.ceil((lossAtParametersToSlots / parameters.count) * 100)
  )

  // Perte entre VenueBookPressed et Parameters (utilisateurs qui cliquent mais n'entrent pas les paramètres)
  const lossAtVenueBookPressedToParameters = Math.max(
    0,
    venueBookPressed.count - parameters.count
  )
  if (lossAtVenueBookPressedToParameters > 0) {
    links.push({
      source: venueBookPressedId,
      target: LOST_NODE_ID,
      value: lossAtVenueBookPressedToParameters,
    })
    setLoss(
      venueBookPressedId,
      Math.ceil(
        (lossAtVenueBookPressedToParameters / venueBookPressed.count) * 100
      )
    )
  } else {
    setLoss(venueBookPressedId, 0)
  }

  const lossAtConfirmToLoadingRecalc = Math.max(
    0,
    confirm.count - loading.count
  )
  setLoss(
    confirmId,
    Math.ceil((lossAtConfirmToLoadingRecalc / confirm.count) * 100)
  )

  const lossAtLoadingToSuccessOrFailureRecalc = Math.max(
    0,
    loading.count - success.count - failure.count
  )
  setLoss(
    loadingId,
    Math.ceil((lossAtLoadingToSuccessOrFailureRecalc / loading.count) * 100)
  )

  // Success = dernière étape, pas de perte
  setLoss(successId, 0)

  const totalLoss =
    lossAtVenueBookPressedToParameters +
    lossAtParametersToSlots +
    lossAtSlotsToConfirm +
    lossAtConfirmToLoading +
    lossAtLoadingToSuccessOrFailure +
    failure.count

  //   console.log('Sankey data:', { nodes, links })
  //   console.log('Total loss:', totalLoss)
  //   console.log('Failure count:', failure.count)
  //   console.log(
  //     'Loss at Loading → Success/Failure:',
  //     lossAtLoadingToSuccessOrFailure
  //   )
  //   console.log('Loss at Slots → Confirm:', lossAtSlotsToConfirm)
  //   console.log('Loss at Parameters → Slots:', lossAtParametersToSlots)
  //   console.log('Loss at Confirm → Loading:', lossAtConfirmToLoading)
  //   console.log(
  //     'Loss at Loading → Success/Failure (recalc):',
  //     lossAtLoadingToSuccessOrFailureRecalc
  //   )

  return {
    nodes: nodes,
    links: links,
    totalLoss,
  }
}
